import { IdentificationCardIcon } from "@phosphor-icons/react/dist/csr/IdentificationCard";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { PageMainCard } from "@volca/ui/components/ui/page-components";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import { canManagePayrollRoster } from "@/payroll/roster/permissions";
import { usePayrollRosterVersion } from "@/payroll/roster/query";
import { RosterReadOnlyView } from "@/payroll/roster/roster-read-only-view";
import { RosterTableSkeleton } from "@/payroll/roster/roster-table-skeleton";
import { PAYROLL_SETTINGS_ROSTER_PATH } from "@/payroll/settings/routes";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { PayBreakdownPaneShell } from "./pay-breakdown-pane-shell";
import { RunSnapshotHeader } from "./run-snapshot-header";

interface PayBreakdownRosterTabProps {
  businessId: string;
  runVersionNumber: number | null;
  currentVersionNumber: number | null;
  hasRun: boolean;
}

export const PayBreakdownRosterTab = ({
  businessId,
  runVersionNumber,
  currentVersionNumber,
  hasRun,
}: PayBreakdownRosterTabProps) => {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const version = runVersionNumber ?? currentVersionNumber;
  const query = usePayrollRosterVersion(businessId, version);
  const { data: roster, isLoading, isError } = currentPayrollResult(query);

  const displayedVersion = roster?.version_number ?? null;
  const displayedIsCurrent =
    displayedVersion != null &&
    currentVersionNumber != null &&
    displayedVersion === currentVersionNumber;
  const displayedIsOlder =
    displayedVersion != null &&
    currentVersionNumber != null &&
    displayedVersion < currentVersionNumber;

  if (version == null) {
    return (
      <PayBreakdownPaneShell fillHeight>
        <Empty className={emptyStateVariants({ layout: "panel" })}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IdentificationCardIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>No payroll roster configured</EmptyTitle>
            <EmptyDescription>
              A roster hasn't been published for this business yet.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </PayBreakdownPaneShell>
    );
  }

  const publishedOn = roster?.published_at
    ? ` Published ${dayjs(roster.published_at).format("MMM D, YYYY")}.`
    : "";
  const description =
    runVersionNumber != null
      ? "Used for this payroll run."
      : hasRun
        ? "This run predates roster versions, so this is the published roster."
        : "The published roster, which the next run will use.";

  return (
    <PayBreakdownPaneShell fillHeight>
      <PageMainCard className="min-w-0">
        <RunSnapshotHeader
          className="px-4"
          badges={
            roster?.version_number != null && (
              <>
                <Badge variant="secondary" size="sm">
                  Roster v{roster.version_number}
                </Badge>
                {displayedIsCurrent && (
                  <Badge variant="secondary" color="success" size="sm">
                    Published
                  </Badge>
                )}
                {displayedIsOlder && (
                  <Badge variant="secondary" color="warning" size="sm">
                    Older version
                  </Badge>
                )}
              </>
            )
          }
          description={`${description}${publishedOn}`}
          action={
            canManagePayrollRoster(selectedBusiness, user)
              ? { to: PAYROLL_SETTINGS_ROSTER_PATH, label: "Edit roster" }
              : undefined
          }
        />

        {isLoading ? (
          <RosterTableSkeleton />
        ) : isError ? (
          <Empty
            className={emptyStateVariants({ layout: "panel" })}
            role="alert"
          >
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <WarningCircleIcon />
              </EmptyMedia>
              <EmptyTitle>Couldn't load this roster version</EmptyTitle>
              <EmptyDescription>Please try again.</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                disabled={query.isFetching}
                onClick={() => void query.refetch()}
              >
                Try again
              </Button>
            </EmptyContent>
          </Empty>
        ) : roster ? (
          <RosterReadOnlyView
            key={`${businessId}-${version}`}
            businessId={businessId}
            roster={roster}
          />
        ) : (
          <Empty className={emptyStateVariants({ layout: "panel" })}>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IdentificationCardIcon aria-hidden />
              </EmptyMedia>
              <EmptyTitle>
                This roster version is no longer available
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}
      </PageMainCard>
    </PayBreakdownPaneShell>
  );
};
